import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const API_BASE = 'https://crm-backend-6jqe.onrender.com';

const HeroBanner = () => {
  const [images, setImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [intervalSec, setIntervalSec] = useState(5);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfName, setPdfName] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const modalRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const progressRef = useRef(null);
  const startTimeRef = useRef(null);

  useEffect(() => {
    const load = () => {
      fetch(`${API_BASE}/api/banners`)
        .then(r => r.json())
        .then(data => {
          if (data.images) setImages(data.images);
          if (data.intervalSec) setIntervalSec(data.intervalSec);
        })
        .catch(() => {});
    };
    load();
    const poll = setInterval(load, 60000);
    return () => clearInterval(poll);
  }, []);

  useEffect(() => {
    if (images.length <= 1) return;
    setProgress(0);
    startTimeRef.current = Date.now();

    const timer = setInterval(() => {
      setCurrentIndex(i => (i + 1) % images.length);
    }, intervalSec * 1000);

    progressRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      setProgress(Math.min((elapsed / (intervalSec * 1000)) * 100, 100));
    }, 50);

    return () => {
      clearInterval(timer);
      clearInterval(progressRef.current);
    };
  }, [images, intervalSec, currentIndex]);

  const goTo = (i) => {
    setCurrentIndex(i);
    setProgress(0);
    startTimeRef.current = Date.now();
  };

  const goPrev = (e) => { e.stopPropagation(); goTo((currentIndex - 1 + images.length) % images.length); };
  const goNext = (e) => { e.stopPropagation(); goTo((currentIndex + 1) % images.length); };

  const handleBannerClick = (img) => {
    if (!img.clickType || img.clickType === 'none') return;
    if (img.clickType === 'url' && img.clickTarget) {
      window.open(img.clickTarget, '_blank', 'noopener,noreferrer');
    } else if (img.clickType === 'pdf' && img.clickTarget) {
      const url = `${API_BASE}${img.clickTarget}`;
      setPdfUrl(url);
      setPdfName(img.name || 'Dokument');
      setPdfLoading(true);
      setPdfBlobUrl(null);
      fetch(url)
        .then(r => r.blob())
        .then(blob => {
          const blobUrl = URL.createObjectURL(blob);
          setPdfBlobUrl(blobUrl);
          setPdfLoading(false);
        })
        .catch(() => setPdfLoading(false));
    }
  };

  const closePdf = () => {
    if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    setPdfUrl(null);
    setPdfBlobUrl(null);
    setPdfLoading(false);
    setNumPages(null);
    setPageNumber(1);
  };

  if (images.length === 0) return null;

  const isClickable = (img) => img.clickType && img.clickType !== 'none' && img.clickTarget;

  return (
    <>
      <div className="w-full max-w-2xl mx-auto mt-6 relative px-9">
        <div className="rounded-2xl overflow-hidden shadow-lg relative">
          {images.map((img, i) => (
            <div
              key={img.id}
              onClick={() => handleBannerClick(img)}
              className={`transition-opacity duration-700 ${i === currentIndex ? 'opacity-100' : 'opacity-0 absolute inset-0 pointer-events-none'} ${isClickable(img) ? 'cursor-pointer' : ''}`}
            >
              <img
                src={`${API_BASE}${img.url}`}
                alt={img.name || `Banner ${i + 1}`}
                className="w-full h-auto block"
              />
            </div>
          ))}

          {images.length > 1 && (
            <>
              <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                {images.map((_, i) => (
                  <button
                    key={i}
                    onClick={(e) => { e.stopPropagation(); goTo(i); }}
                    className={`rounded-full transition-all duration-300 ${i === currentIndex ? 'w-5 h-2 bg-white' : 'w-2 h-2 bg-white/50 hover:bg-white/75'}`}
                    aria-label={`Banner ${i + 1}`}
                  />
                ))}
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/10 z-10">
                <div className="h-full bg-red-500 transition-none" style={{ width: `${progress}%` }} />
              </div>
            </>
          )}
        </div>

        {images.length > 1 && (
          <>
            <button onClick={goPrev} className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white shadow-md hover:bg-gray-100 transition-colors text-gray-700" aria-label="Předchozí">
              <ChevronLeft size={18} />
            </button>
            <button onClick={goNext} className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white shadow-md hover:bg-gray-100 transition-colors text-gray-700" aria-label="Další">
              <ChevronRight size={18} />
            </button>
          </>
        )}
      </div>

      {pdfUrl && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={closePdf}>
          <div
            ref={modalRef}
            className="relative bg-white rounded-2xl overflow-hidden shadow-2xl w-full max-w-xl flex flex-col"
            style={{ maxHeight: '92vh' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 flex-shrink-0">
              {numPages > 1 && (
                <div className="flex items-center gap-2">
                  <button onClick={() => setPageNumber(p => Math.max(1, p - 1))} disabled={pageNumber <= 1} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 disabled:opacity-30 transition-colors">
                    <ChevronLeft size={16} />
                  </button>
                  <span className="text-xs text-gray-500">{pageNumber} / {numPages}</span>
                  <button onClick={() => setPageNumber(p => Math.min(numPages, p + 1))} disabled={pageNumber >= numPages} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 disabled:opacity-30 transition-colors">
                    <ChevronDown size={16} className="rotate-[-90deg]" />
                  </button>
                </div>
              )}
              {!(numPages > 1) && <div />}
              <button onClick={closePdf} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors text-gray-500">
                <X size={18} />
              </button>
            </div>

            {/* PDF content */}
            <div className="overflow-y-auto flex-1 flex justify-center bg-gray-50 p-2">
              {pdfLoading && (
                <div className="flex items-center justify-center py-20">
                  <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
              {pdfBlobUrl && (
                <Document
                  file={pdfBlobUrl}
                  onLoadSuccess={({ numPages }) => { setNumPages(numPages); setPdfLoading(false); }}
                  loading={null}
                >
                  <Page
                    pageNumber={pageNumber}
                    width={Math.min(window.innerWidth - 64, 560)}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                </Document>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HeroBanner;
export const BANNER_API_BASE = API_BASE;
